import clsx from 'clsx'
import { Dispatch, SetStateAction } from 'react'
import React from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'

import s from './styles.module.scss'

import ModalSelectProposalFooterTotal from './modal-select-proposal-footer-total/ModalSelectProposalFooterTotal'
import {
  MODAL_SELECT_PROPOSAL_MODE,
  StepEnum,
} from '../modal-select-proposal.enum'
import { ISelectProposalHook } from '../modal-select-proposal.types'
import { PriceObjectOutputDto } from '../../../../lib/graphql'
import { Button, IconSvg, ToggleButton } from '../../../atoms'
import { usePriceContext, VAT_STATUS } from '../../../contexts'

const getButtonLabel = ({
  page,
  mode,
  isAutonomousBookingAvailable,
  intl,
}: {
  page: StepEnum
  mode: MODAL_SELECT_PROPOSAL_MODE
  isAutonomousBookingAvailable?: boolean
  intl: IntlShape
}) => {
  switch (page) {
    case StepEnum.SELECT_OPTIONS:
      if (mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD) {
        if (isAutonomousBookingAvailable !== false) {
          return intl.formatMessage({
            id: 'ui.ModalSelectProposal.getButtonLabel.continueAndChose',
            defaultMessage: 'Continuer et choisir une formule',
          })
        } else {
          return intl.formatMessage({
            id: 'ui.ModalSelectProposal.getButtonLabel.createQuoteInMySpace',
            defaultMessage: 'Créer un devis dans mon espace',
          })
        }
      } else {
        return intl.formatMessage({
          id: 'ui.ModalSelectProposal.getButtonLabel.continue',
          defaultMessage: 'Continuer',
        })
      }
    case StepEnum.SELECT_CLIENT_REQUEST:
      return intl.formatMessage({
        id: 'ui.ModalSelectProposal.getButtonLabel.createQuote',
        defaultMessage: 'Créer le devis',
      })
    default:
      return intl.formatMessage({
        id: 'ui.ModalSelectProposal.getButtonLabel.continue',
        defaultMessage: 'Continuer',
      })
  }
}

interface IModalSelectProposalFooterProps {
  setVatStatus: Dispatch<SetStateAction<VAT_STATUS>>
  page: StepEnum
  total: Pick<PriceObjectOutputDto, 'priceWithDiscountAndFees'> | undefined
  adults?: number
  isButtonLoading?: boolean
  mode: MODAL_SELECT_PROPOSAL_MODE
  handleNextStepChange: () => void
  isButtonActive: boolean
  isAutonomousBookingAvailable?: boolean
  isAuthenticated: ISelectProposalHook['isAuthenticated']
  isLowRes?: boolean
  backoffice?: boolean
}

export const ModalSelectProposalFooter = ({
  setVatStatus,
  page,
  total,
  adults,
  isButtonLoading = false,
  mode,
  handleNextStepChange,
  isButtonActive,
  isAutonomousBookingAvailable,
  isAuthenticated,
  isLowRes = false,
  backoffice = false,
}: IModalSelectProposalFooterProps) => {
  const intl = useIntl()
  const { vatStatus } = usePriceContext()

  if (isLowRes) {
    return (
      <div
        className={clsx(
          s.footerContainer,
          s.containerHouseCard,
          s.lowResFooter,
        )}
      >
        <div className={s.containerTotal}>
          <ModalSelectProposalFooterTotal
            displayPricesAsEstimates={isAutonomousBookingAvailable}
            total={total}
            adults={adults}
            mode={mode}
            isAuthenticated={isAuthenticated}
            backoffice={backoffice}
          />
        </div>

        <div
          className={clsx(s.detailsContainer, {
            [s.detailsContainerFormules]: page == StepEnum.SELECT_MENU,
          })}
          style={{ padding: page !== StepEnum.SELECT_MENU ? '20px 0' : '' }}
        >
          {isAutonomousBookingAvailable ? (
            <div className={s.switchContainer}>
              <ToggleButton
                nameLeft={
                  <FormattedMessage
                    id='ui.ModalSelectProposalFooter.exclVat'
                    defaultMessage='HT'
                  />
                }
                nameRight={
                  <FormattedMessage
                    id='ui.ModalSelectProposalFooter.inclVat'
                    defaultMessage='TTC'
                  />
                }
                value={vatStatus === VAT_STATUS.HT}
                setValue={(value) =>
                  setVatStatus(value ? VAT_STATUS.HT : VAT_STATUS.TTC)
                }
                className={s.vatSwitch}
              />
            </div>
          ) : null}

          <div className={s.buttonContainer}>
            <Button
              className={clsx(s.button, { [s.buttonDisable]: !isButtonActive })}
              onClick={handleNextStepChange}
              disabled={!isButtonActive}
              loading={isButtonLoading}
            >
              <div className={s.buttonChildrenContainer}>
                <p>
                  {getButtonLabel({
                    page,
                    mode,
                    isAutonomousBookingAvailable,
                    intl,
                  })}
                </p>
                <IconSvg.ArrowRight color='currentColor' />
              </div>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(s.footerContainer, {
        [s.containerHouseCard]:
          mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD,
      })}
    >
      {mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD && (
        <div className={s.containerTotal}>
          <ModalSelectProposalFooterTotal
            displayPricesAsEstimates={isAutonomousBookingAvailable}
            total={total}
            adults={adults}
            mode={mode}
            isAuthenticated={isAuthenticated}
          />
        </div>
      )}

      <div className={s.detailsContainer}>
        {isAutonomousBookingAvailable ? (
          <div className={s.switchContainer} data-gtm='toggle-VAT'>
            <ToggleButton
              dataMixpanel={{ name: 'toggle-VAT' }}
              nameLeft={
                <FormattedMessage
                  id='ui.ModalSelectProposalFooter.exclVat'
                  defaultMessage='HT'
                />
              }
              nameRight={
                <FormattedMessage
                  id='ui.ModalSelectProposalFooter.inclVat'
                  defaultMessage='TTC'
                />
              }
              value={vatStatus === VAT_STATUS.HT}
              setValue={(value) =>
                setVatStatus(value ? VAT_STATUS.HT : VAT_STATUS.TTC)
              }
              className={s.vatSwitch}
            />
          </div>
        ) : null}

        {mode === MODAL_SELECT_PROPOSAL_MODE.DEFAULT && (
          <ModalSelectProposalFooterTotal
            displayPricesAsEstimates={isAutonomousBookingAvailable}
            total={total}
            adults={adults}
            mode={mode}
            isAuthenticated={isAuthenticated}
          />
        )}
        <div className={s.buttonContainer}>
          <Button
            className={clsx(s.button, { [s.buttonDisable]: !isButtonActive })}
            onClick={handleNextStepChange}
            disabled={!isButtonActive}
            loading={isButtonLoading}
            dataMixpanel={{
              name: 'Autonomous Booking',
              data: {
                label: getButtonLabel({
                  page,
                  mode,
                  isAutonomousBookingAvailable,
                  intl,
                }),
                page,
              },
            }}
            data-testid='new-proposal-next-button'
          >
            <div className={s.buttonChildrenContainer}>
              <p>
                {getButtonLabel({
                  page,
                  mode,
                  isAutonomousBookingAvailable,
                  intl,
                })}
              </p>
              <IconSvg.ArrowRight color='currentColor' />
            </div>
          </Button>
        </div>
      </div>
    </div>
  )
}
